import { useState } from "react";
import { Dialog, DialogTitle, DialogHeader, DialogContent, DialogDescription, DialogFooter } from "../ui/dialog";
import { DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { TrashIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDeletePetiteAnnonce } from "../../lib/hooks/usePetitesAnnonces";
import { ksPetitesAnnoncesQK } from "../../lib/query-keys";
import { toast } from "sonner";
import { EtatPetiteAnnonce } from "../../lib/enums/etat-petite-annonce.enum";
import { PetiteAnnonce } from "../../lib/interfaces/petite-annonce.interface";

interface Props {
  petiteAnnonce: PetiteAnnonce;
}

export default function DeletePetiteAnnonceButton({ petiteAnnonce }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useDeletePetiteAnnonce();

  const handleDelete = () =>
    deleteMutation.mutate(petiteAnnonce.id, {
      onSuccess: () => {
        toast.success("Petite annonce supprimée avec succès");
        const oldData = queryClient.getQueryData<PetiteAnnonce[]>([ksPetitesAnnoncesQK]);
        if (oldData) {
          queryClient.setQueryData<PetiteAnnonce[]>(
            [ksPetitesAnnoncesQK],
            oldData.filter((p) => p.id !== petiteAnnonce.id)
          );
        }
        queryClient.invalidateQueries({ queryKey: [ksPetitesAnnoncesQK] });
        setIsOpen(false);
      },
      onError: (error) => {
        console.error(error);
        toast.error("Une erreur est survenue lors de la suppression de la petite annonce");
      },
    });

  return (
    <div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            disabled={deleteMutation.isPending || petiteAnnonce.etat !== EtatPetiteAnnonce.AttentePaiement}
            variant='destructive'
            size='icon'
          >
            <TrashIcon size={16} />
          </Button>
        </DialogTrigger>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[500px] sm:rounded-2xl">
          <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5 pr-12">
            <DialogTitle className="text-xl text-slate-950">Supprimer la petite annonce</DialogTitle>
          </DialogHeader>
          <DialogDescription className="px-6 py-5 leading-6 text-slate-600">
            Êtes-vous sûr de vouloir supprimer cette petite annonce ? Cette action est irréversible.
          </DialogDescription>
          <DialogFooter className="border-t border-slate-100 bg-slate-50/70 px-6 py-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
